/**
 * A gardener's public garden page: the one page "Public" points at.
 *
 * Every gardener's page has a readable address, grow.gardengameplan.com/@amy,
 * given by the backend the first time the page is needed (getMyGarden,
 * making something public, a share naming its gardener), and theirs to
 * change. The "@" is the address's form, as a handle reads; the backend
 * keeps "amy". The page also has a random code, /g/<code>, which keeps
 * working for old links and moves a visitor on to the address. Links are
 * built with the address (gardenPathFor, gardenUrlFor), falling back to the
 * code only while there is none (giving one failed).
 *
 * The page opens with the gardener's profile photo and name; its plant and
 * bed cards scroll through photos (GardenPhoto). The page's cover (the
 * gardener's newest public photo) leads the Gardens directory card and link
 * previews; there is no cover to pick any more (owner, 2026-10-14).
 *
 * Each public plant and bed has a page of its own under the address
 * (/@amy/cherokee-purple, getGardenItem). Opening any of these pages counts
 * one visit per browser, as share pages do (a seen list in localStorage,
 * and ?count=0 to the backend on a return).
 *
 * Endpoints (the public pages send lang, the app's language, so each plant's
 * names come in it: services/grow/plantNaming):
 * - GET /gardens/mine → MyGarden fields (signed in).
 * - GET /gardens/:code[?count=0&lang=] → PublicGarden (no sign-in).
 * - GET /gardens/by-slug/:slug[?count=0&lang=] → PublicGarden (no sign-in); 404.
 * - GET /gardens/by-slug/:slug/:item[?count=0&lang=] → GardenItem (no sign-in); 404.
 * - GET /gardens/directory?sort=&zip=&page= → GardenDirectoryPage (no sign-in): Gardens on Grow.
 * - GET /gardens/directory/plants?q=&page=&lang= → DirectoryPlantsPage (no sign-in): its plant search.
 * - GET /gardens/address/check?slug= → GardenAddressCheck (signed in).
 * - PUT /gardens/mine/address { slug } → { slug, path }; 400 or 409 with
 *   `reason` (addressRefusal).
 * - PUT /gardens/mine/dates { day, year } → { dates } (signed in): which parts
 *   of a date the pages show (setMyDateParts).
 *
 * Who uses it: features/share (GardenPage, GardenItemPage, SharePage,
 * GardensPage, gardenPage, gardenPageParts) for the public pages, and the profile's
 * PublicPageCard and GardenPageRow for the gardener's own. Caching:
 * MY_GARDEN_QUERY_KEY ('my-garden', MY_GARDEN_STALE_MS) for the gardener's
 * own; the address check under ['garden-address-check', slug] with no
 * staleness. The public pages are fetched by their pages directly. Auth:
 * the public reads send a token only if someone happens to be signed in,
 * and need none.
 *
 * @module gardens
 */

import { apiClient } from './apiClient';
import { shareBaseUrl } from './shares';
import type { LinkKey } from '@/shared/lib/socialLinks';
import type { PlantNames } from '@/shared/types/plant';
import type { DateParts } from '@/shared/utils/dateUtils';
import { getLanguage } from '@/shared/i18n/i18nSetup';
import {
  SEEN_GARDENS_MAX, SEEN_GARDENS_STORAGE_KEY,
} from '@/config/tunables/garden';

/** One photo on a card: its API path, and the day it was taken (none for a bed's own picture). */
export interface GardenPhoto {
  /** The photo's API path ("/api/…") or full address: its card copy (800x600). */
  src: string;
  /**
   * The same photo's large copy (about 1600px; D53), which a plant's, bed's or
   * share page draws on a wide screen. Absent on a bed's own picture and on
   * a seed-packet picture; then src is drawn everywhere.
   */
  large?: string;
  /** The day it was taken (YYYY-MM-DD), or null. */
  date: string | null;
  /** True for a plant's seed-packet picture (shown last, or alone when it has no garden photo); the card labels it "Seed packet". */
  packet?: true;
  /** On a bed's card, the plant this photo is of ("Romaine Lettuce"); absent on the bed's own picture and on a plant's card. */
  plant?: string;
  /** On a bed's card, that plant's stage now, for the card to write over the photo. */
  stage?: GardenStage | null;
}

/** A bed on a garden page: its cover, and its own page under the garden's address. */
export interface GardenBed {
  /** The bed's name. */
  name: string;
  /** Its description, if the gardener wrote one. */
  description: string | null;
  /** Its cover picture's address, if it has one. */
  cover: string | null;
  /** Its newest standing share link's code, if any. */
  code: string | null;
  /** Its own address under the garden's (/@amy/<slug>), or null. */
  slug: string | null;
  /** How many public plants it holds. */
  plants?: number;
  /** A few of their crops, most plants first ("tomatoes", "peppers"). */
  crops?: string[];
  /** What its card scrolls through: the bed's own picture, then the latest photo of each public plant in it. */
  photos: GardenPhoto[];
}

/**
 * A stage as a public page names it: its name, said by the server in the visitor's language (the catalogue stage's
 * word, or the gardener's own name for a stage they made or renamed; bloom-backend services/stages/stageKinds), and
 * its catalogue type.
 */
export interface GardenStage {
  /** SEEDING, MATURITY, ...; null for a stage the gardener made or renamed. Not needed to name it. */
  type: string | null;
  /** The name to print, as sent. */
  name: string;
  /** A point the plant ended at (Last Harvest, Expired): said with its day, never "since" it. */
  ended?: boolean;
}

/** A plant on a garden page: its newest shared photo, its stage, and its own page. */
export interface GardenPlant {
  /** The plant's kind ("Romaine Lettuce"); never shown beside its name. */
  name: string;
  /** Its one name, whole ("Devil's Ear Lettuce"; services/grow/plantNaming), in the page's language. */
  variety: string;
  /** How every page says it (services/grow/plantNaming). */
  names: PlantNames;
  /** Its bed's name, only when that bed is public. */
  bed: string | null;
  /** Its newest standing share link's code, if any. */
  code: string | null;
  /** Its own address under the garden's (/@amy/<slug>), or null. */
  slug: string | null;
  /** Its bed's own address, when the bed is public. */
  bedSlug?: string | null;
  /** Its crop's plural name ("tomatoes"). */
  crop?: string | null;
  /** Its newest shared photo: API paths for the thumbnail and the card, and the day it was taken. */
  photo?: { thumb: string; card: string; date: string | null } | null;
  /** Its newest shared photos, newest first, for its card to scroll through. */
  photos: GardenPhoto[];
  /** The day of that photo, else of its newest recorded stage (YYYY-MM-DD). */
  date?: string | null;
  /** The stage it is at now. */
  stage?: GardenStage | null;
  /** How many saved Plant Doctor checkups its page shows; absent or 0 for none. */
  checkupCount?: number;
}

/** A saved Plant Doctor checkup on a plant's public page: never the gardener's own words. */
export interface PublicCheckup {
  /** The checkup's id. */
  id: number;
  /** The checkup's day (YYYY-MM-DD). */
  date: string;
  /** Its newest photo, as an API path with the day it was taken; null when none may be shown. */
  photo: { src: string; date: string | null } | null;
  /** The likely problem's name, in each language. */
  problem: { en: string; es: string };
  /** The problem's first step from the library, in each language; null without one. */
  step: { en: string; es: string } | null;
  /** The problem's library page, when one exists (none does yet). */
  aboutUrl?: string | null;
}

/** The gardener's name and the link they are known by. */
export interface GardenGardener {
  name: string;
  /** The profile link the name is, when "Show me as" is one; null for the display name. */
  place: LinkKey | null;
}

/** What a garden page shows: whose it is, while public, and its beds and plants. */
export interface PublicGarden {
  /** The page's random code (/g/<code>). */
  code: string;
  /** The gardener's own address, when they have chosen one; the page moves there. */
  slug?: string | null;
  /** The gardener's name and the link they are known by, or null when not shown. */
  gardener: GardenGardener | null;
  /** Their USDA hardiness zone ("7b"); never the town or ZIP. */
  zone?: string | null;
  /** Their profile photo's API path, or null (the page then shows their initial). */
  avatar?: string | null;
  /** The page's cover photo: their newest public photo (not shown on the page; the Gardens card and link previews use it); null with none. */
  cover?: GardenPhoto | null;
  /** The places they can be found, as web addresses, in the profile's order. */
  links?: Array<{ key: LinkKey; url: string }>;
  /** The words of their newest share with its day and what it was of; null with none. */
  latest?: { words: string; date: string | null; plant: string | null; bed: string | null; slug: string | null } | null;
  /** Their season in numbers: public plants, public beds, the first harvest recorded this year. */
  season?: { plants: number; beds: number; firstHarvest: string | null } | null;
  /** Their season review's picture, when it went out with its Public switch on. */
  review?: { code: string; year: number; frame: string; size?: string | null } | null;
  /** Its public beds. */
  beds: GardenBed[];
  /** Its public plants. */
  plants: GardenPlant[];
  /** Which parts of a date the gardener's pages show (Profile > Public pages). */
  dates: DateParts;
}

/** A plant's own page under a garden page (/@amy/cherokee-purple). */
export interface GardenPlantPage extends GardenPlant {
  /** The newest share picture it went out with, else its newest shared photo; an API path. */
  picture: { kind: 'frame' | 'photo'; url: string; code: string | null; size?: string | null; packet?: true } | null;
  /** The gardener's words from its newest share that has some. */
  words: string | null;
  /** The day (YYYY-MM-DD) of the share those words are from, or null. */
  wordsDate: string | null;
  /** Its recorded stages, oldest first. */
  timeline: Array<{ on: string; stage: GardenStage }>;
  /** The day (YYYY-MM-DD) its stage now began, when its newest recorded stage is that stage (the server decides); null or absent otherwise. */
  stageSince?: string | null;
  /** Its saved Plant Doctor checkups the gardener has not hidden, newest first. */
  checkups: PublicCheckup[];
  /** A few more of the garden's plants, and how many beside them. */
  more: {
    /** Each by its one name (variety; names from the naming backend) with its newest shown photo (an API path). */
    plants: Array<{ variety: string; name: string; names: PlantNames; slug: string | null; photo?: string | null }>;
    rest: number;
  };
}

/** What a plant's or bed's own page shows, with whose garden it is in. */
export type GardenItem = {
  code: string;
  slug: string;
  gardener: GardenGardener | null;
  zone: string | null;
  /** Which parts of a date the gardener's pages show. */
  dates: DateParts;
} & (
  | { kind: 'plant'; plant: GardenPlantPage }
  | { kind: 'bed'; bed: Omit<GardenBed, 'plants'> & { plants: GardenPlant[]; words?: string | null; wordsDate?: string | null } }
);

/** The gardener's own garden page, as Profile > Public pages shows it. */
export interface MyGarden {
  /** The random code: /g/<code> always works. */
  code: string;
  /** The page's relative address: /@<slug> with an address, else /g/<code>. */
  path: string;
  /** Their address, kept without its "@"; given by the backend, so null only if giving one failed. */
  slug: string | null;
  /** Which parts of a date their public pages and shares show. */
  dates: DateParts;
  /** The address rules the server holds (Grow settings address.*): the field's limit and the lines about length and the hold. */
  rules: GardenAddressRules;
}

/** The rules for an address, as the server's Grow settings have them. */
export interface GardenAddressRules {
  /** The fewest characters an address may have, without the "@". */
  minLength: number;
  /** The most characters an address may have. */
  maxLength: number;
  /** The days an address a gardener gave up is kept from anyone else. */
  holdDays: number;
}

/** Why an address cannot be had: its shape, one of Grow's own names, a blocked word, or another gardener. */
export type GardenAddressProblem = 'short' | 'long' | 'characters' | 'hyphen' | 'reserved' | 'blocked' | 'taken' | 'held';

/** Whether an address could be the gardener's, as the backend answers. */
export interface GardenAddressCheck {
  /** The address as the backend folded it. */
  slug: string;
  /** Whether they could take it (or already have it). */
  available: boolean;
  /** Already theirs. */
  mine: boolean;
  /** Why not; null when available. */
  reason: GardenAddressProblem | null;
  /** The server's address rules, for wording a 'short' or 'long' reason. */
  rules: GardenAddressRules;
}

/**
 * The gardener's own garden link, its code and address given the first
 * time it is asked for.
 *
 * @returns The page's code, address, path, date parts and address rules.
 * @throws The request's error (a failure on our side is a 500 the caller says with the standard sentence).
 */
export async function getMyGarden(): Promise<MyGarden> {
  const response = await apiClient.get<MyGarden & { success: boolean }>('/gardens/mine');
  const data = response.data;
  return {
    code: data.code,
    path: data.path,
    slug: data.slug ?? null,
    dates: { day: data.dates.day, year: data.dates.year },
    rules: data.rules,
  };
}

/**
 * Keeps which parts of a date the gardener's public pages and shares show
 * (Profile > Public pages).
 *
 * @param parts - Whether to show the day of the month, and the year.
 * @returns The parts kept.
 * @throws The request's error.
 */
export async function setMyDateParts(parts: DateParts): Promise<DateParts> {
  const response = await apiClient.put<{ success: boolean; dates: DateParts }>('/gardens/mine/dates', { day: parts.day, year: parts.year });
  return response.data.dates;
}

/**
 * Whether this browser has opened this page before, noting it if not, as
 * share pages do (shares firstVisit): a visit is one person opening a page,
 * not every load of it.
 *
 * @param key - The page: "g:<code>", "@<slug>" or "@<slug>/<item>".
 * @returns True the first time; also true when storage is refused, so the visit counts.
 */
function firstVisit(key: string): boolean {
  try {
    const seen: string[] = JSON.parse(window.localStorage.getItem(SEEN_GARDENS_STORAGE_KEY) || '[]');
    if (seen.includes(key)) return false;
    window.localStorage.setItem(SEEN_GARDENS_STORAGE_KEY, JSON.stringify([...seen, key].slice(-SEEN_GARDENS_MAX)));
    return true;
  } catch {
    return true;
  }
}

/**
 * Notes a page as opened without asking: the other address of a garden just
 * loaded (its code, or its slug), so moving from /g/<code> to /@<slug> is
 * one visit.
 *
 * @param key - As firstVisit takes it.
 */
function noteVisit(key: string): void {
  firstVisit(key);
}

/**
 * A garden page's data, by its code. No account needed. Counts the visit
 * once per browser.
 *
 * @param code - The page's code, from the address (/g/:code); encoded, since
 *   it is whatever was typed or pasted there.
 */
export async function getGarden(code: string): Promise<PublicGarden> {
  const count = firstVisit(`g:${code}`);
  const response = await apiClient.get<PublicGarden>(`/gardens/${encodeURIComponent(code)}`, { params: { ...(count ? {} : { count: 0 }), lang: getLanguage() } });
  if (response.data.slug) noteVisit(`@${response.data.slug}`);
  return response.data;
}

/**
 * A garden page's data, by the gardener's own address. No account needed.
 * Counts the visit once per browser.
 *
 * @param slug - The address, from the path (/@:slug) without its "@"; folded and lower-cased by the backend too.
 * @throws When no one has the address (404).
 */
export async function getGardenBySlug(slug: string): Promise<PublicGarden> {
  const count = firstVisit(`@${slug}`);
  const response = await apiClient.get<PublicGarden>(`/gardens/by-slug/${encodeURIComponent(slug)}`, { params: { ...(count ? {} : { count: 0 }), lang: getLanguage() } });
  if (response.data.code) noteVisit(`g:${response.data.code}`);
  return response.data;
}

/**
 * A plant's or bed's own page under a garden page. No account needed.
 * Counts the visit once per browser.
 *
 * @param slug - The garden's address, without its "@".
 * @param item - The plant's or bed's address.
 * @throws When there is no such garden or no public plant or bed has the address (404).
 */
export async function getGardenItem(slug: string, item: string): Promise<GardenItem> {
  const count = firstVisit(`@${slug}/${item}`);
  const response = await apiClient.get<GardenItem>(
    `/gardens/by-slug/${encodeURIComponent(slug)}/${encodeURIComponent(item)}`,
    { params: { ...(count ? {} : { count: 0 }), lang: getLanguage() } }
  );
  return response.data;
}

/**
 * The path of a plant's or bed's own page: /@<slug>/<item>.
 *
 * @param slug - The garden's address.
 * @param item - The plant's or bed's address.
 */
export function gardenItemPathFor(slug: string, item: string): string {
  return `/@${slug}/${item}`;
}

/**
 * Asks whether the signed-in gardener could take an address.
 *
 * @param slug - As typed; the backend normalises it (trims, drops a leading "@", folds accents, lower-cases)
 *   and answers whether it is acceptable and free: the app keeps none of the address rules (audit A43).
 */
export async function checkGardenAddress(slug: string): Promise<GardenAddressCheck> {
  const response = await apiClient.get<GardenAddressCheck & { success: boolean }>('/gardens/address/check', { params: { slug } });
  const { slug: checked, available, mine, reason, rules } = response.data;
  return { slug: checked, available, mine, reason, rules };
}

/**
 * Changes the signed-in gardener's address. The old one stops working at
 * once, and is held for them for the server's address.HOLD_DAYS (MyGarden.rules.holdDays).
 *
 * @param slug - The new address.
 * @returns The address now held, and the page's relative path.
 * @throws The request's error: 400 (shape, reserved or blocked) and 409
 *   (taken or held) carry `reason` in their body (addressRefusal reads it).
 */
export async function setGardenAddress(slug: string): Promise<{ slug: string | null; path: string }> {
  const response = await apiClient.put<{ success: boolean; slug: string | null; path: string }>('/gardens/mine/address', { slug });
  return { slug: response.data.slug, path: response.data.path };
}

/**
 * Why the backend refused an address, from a failed setGardenAddress.
 *
 * @param err - What it threw.
 * @returns The reason, or null for any other failure.
 */
export function addressRefusal(err: unknown): GardenAddressProblem | null {
  const data = (err as { response?: { status?: number; data?: { reason?: unknown } } })?.response;
  if (!data || (data.status !== 400 && data.status !== 409)) return null;
  const reason = data.data?.reason;
  return typeof reason === 'string' ? (reason as GardenAddressProblem) : null;
}

/**
 * A garden page's path: /@<slug> with the gardener's own address, else
 * /g/<code>. The backend's gardenPath makes the same.
 *
 * @param code - The page's code.
 * @param slug - The gardener's own address, kept without its "@", if any.
 */
export function gardenPathFor(code: string, slug?: string | null): string {
  return slug ? `/@${slug}` : `/g/${code}`;
}

/**
 * The full address of a garden page, as it is sent: on the share links'
 * address (shares.shareBaseUrl, the backend's previews when configured,
 * which answer /@<slug> as well as /g/<code>), else this app's own host.
 * The gardener's own address when they have one, else /g/<code>.
 *
 * @param code - The page's code.
 * @param slug - The gardener's own address, if any.
 */
export function gardenUrlFor(code: string, slug?: string | null): string {
  return `${shareBaseUrl()}${gardenPathFor(code, slug)}`;
}

/** The orders Gardens on Grow may be listed in. */
export type DirectorySort = 'recent' | 'plants' | 'near';

/** A garden in the Gardens on Grow listing: only what its own public page says. */
export interface DirectoryGarden {
  /** The gardener's address, without its "@" (the page is /@<slug>). */
  slug: string;
  /** The name the gardener chose to show, or null. */
  name: string | null;
  /** How many public plants. */
  plants: number;
  /** How many public beds. */
  beds: number;
  /** The day (YYYY-MM-DD) of their newest standing share, or null. */
  updated: string | null;
  /** Their cover photo, or null for a leaf tile. */
  cover: GardenPhoto | null;
  /** Up to five of their plants' photos for the card's collage, the cover first. */
  photos: GardenPhoto[];
}

/** One page of the listing. */
export interface GardenDirectoryPage {
  /** How many gardens are listed in all. */
  total: number;
  /** This page, from 1. */
  page: number;
  /** How many a page holds. */
  pageSize: number;
  /** This page's gardens. */
  gardens: DirectoryGarden[];
  /** For sort "near" only: whether the ZIP was found; false means the list is Recently updated. */
  zipKnown?: boolean;
}

/** A plant found by a search across the public gardens. */
export interface DirectoryPlant {
  /** The plant's kind ("Slicing Cucumber"); never shown beside its name. */
  name: string;
  /** Its one name, whole ("Marketmore 76 Cucumber"), or null. */
  variety: string | null;
  /** How every page says it (services/grow/plantNaming). */
  names: PlantNames;
  /** Its stage now, or null. */
  stage: GardenStage | null;
  /** Its newest public photo, or null for a leaf tile. */
  photo: GardenPhoto | null;
  /** Its own address under its garden's (/@<garden>/<slug>), or null when none could be worked out. */
  slug: string | null;
  /** The garden it grows in. */
  garden: { slug: string };
}

/** One page of a plant search. */
export interface DirectoryPlantsPage {
  /** How many public plants matched in all. */
  total: number;
  /** How many gardens those plants are in. */
  gardens: number;
  /** This page, from 1. */
  page: number;
  /** How many a page holds. */
  pageSize: number;
  /** This page's plants. */
  plants: DirectoryPlant[];
}

/**
 * One page of Gardens on Grow, the public listing. No account needed; counts
 * no visit.
 *
 * @param options.sort - The order.
 * @param options.zip - The five-digit ZIP the visitor typed, for sort 'near'; used for this read only.
 * @param options.page - From 1.
 * @returns The page.
 * @throws The request's error.
 */
export async function getGardenDirectory(options: { sort: DirectorySort; zip?: string | null; page: number }): Promise<GardenDirectoryPage> {
  const params: Record<string, string | number> = { sort: options.sort, page: options.page };
  if (options.sort === 'near' && options.zip) params.zip = options.zip;
  const response = await apiClient.get<GardenDirectoryPage>('/gardens/directory', { params });
  return response.data;
}

/**
 * One page of a plant search across the public gardens. No account needed;
 * counts no visit.
 *
 * @param options.q - What to look for.
 * @param options.page - From 1.
 * @returns The page.
 * @throws The request's error.
 */
export async function searchDirectoryPlants(options: { q: string; page: number }): Promise<DirectoryPlantsPage> {
  const response = await apiClient.get<DirectoryPlantsPage>('/gardens/directory/plants', { params: { q: options.q, page: options.page, lang: getLanguage() } });
  return response.data;
}
